<template>
    <lb-dialog 
        :value="value" 
        @input="$emit('input', $event)"
        heading="Details" 
        width="50%" 
        v-if="value"
        data-testid="report-dialog"
    >
        <template v-slot:body>
            <h5 data-testid="report-dialog__title">{{ title }}</h5>
            <div class="pt-4" data-testid="report-dialog__content">
                <div class="d-flex flex-column gap-4">
                    <!-- Custom header fields slot (before failed reason) -->
                    <slot name="header-fields" :item="detailsItem"></slot>

                    <!-- Failed Reason (common) -->
                    <v-row no-gutters class="mb-2 align-center" v-if="detailsItem.stage === -1" data-testid="report-dialog__failed-reason-row">
                        <v-col cols="12" sm="4" class="details-label failed-reason" data-testid="report-dialog__failed-reason-label">Failed Reason</v-col>
                        <v-col cols="12" sm="8" class="details-value font-weight-semibold failed-reason" data-testid="report-dialog__failed-reason-value">
                            {{ detailsItem.errormessage }}
                        </v-col>
                    </v-row>

                    <!-- Custom middle fields slot (after failed reason, before RU) -->
                    <slot name="middle-fields" :item="detailsItem"></slot>

                    <!-- RU (common with customizable condition) -->
                    <v-row no-gutters class="mb-2 align-center" v-if="detailsItem.reportingunit_detail" data-testid="report-dialog__ru-row">
                        <v-col cols="12" sm="4" class="details-label" data-testid="report-dialog__ru-label">{{ $nova.labelsName('reports_page','commentary_ru_label','RU') }}</v-col>
                        <v-col cols="12" sm="8" class="details-value font-weight-semibold">
                            <div v-if="detailsItem.reportingunit_detail[0]?.name === 'All'" data-testid="report-dialog__ru-all">
                                All
                            </div>
                            <ExpandableList
                                v-else
                                :items="detailsItem.reportingunit_detail"
                                :limit="3"
                                containerClass="d-flex flex-wrap gap-2"
                                toggleButtonClass="ml-1 mb-2 primary"
                                :inline="true"
                                dataTestId="report-dialog__ru-toggle"
                            >
                                <template #default="{ displayedItems }">
                                    <v-chip 
                                        class="chip-choose" 
                                        label 
                                        :data-testid="`${testIdPrefix}__ru-chip`"
                                        v-for="ru in displayedItems" 
                                        :key="ru.id"
                                    >
                                        <span class="categorystype">{{ ru.code }} - {{ ru.name }}</span>
                                    </v-chip>
                                </template>
                            </ExpandableList>
                        </v-col>
                    </v-row>

                    <!-- Custom after-ru fields slot (after RU, before RU Type) -->
                    <slot name="after-ru-fields" :item="detailsItem"></slot>

                    <!-- RU Type (common, but optional based on slot usage) -->
                    <v-row no-gutters class="mb-2 align-center" v-if="showRuType && detailsItem.ru_type !== undefined" data-testid="report-dialog__ru-type-row">
                        <v-col cols="12" sm="4" class="details-label" data-testid="report-dialog__ru-type-label">{{ $nova.labelsName('reports_page','commentary_ru_label','RU') }} Type</v-col>
                        <v-col cols="12" sm="8" class="details-value font-weight-semibold" data-testid="report-dialog__ru-type-value">
                            <span v-if="detailsItem.ru_type === 'ru'">RU</span>
                            <span v-else-if="detailsItem.ru_type === 'jv'">JV</span>
                            <span v-else-if="detailsItem.ru_type === 'soxid'">SOX ID</span>
                            <span v-else>All</span>
                        </v-col>
                    </v-row>

                    <!-- Network (common) -->
                    <v-row no-gutters class="mb-2 align-center" v-if="detailsItem.network_detail" data-testid="report-dialog__network-row">
                        <v-col cols="12" sm="4" class="details-label" data-testid="report-dialog__network-label">{{ $nova.labelsName('reports_page','division_label','Network') }}</v-col>
                        <v-col cols="12" sm="8" class="details-value font-weight-semibold">
                            <ExpandableList
                                :items="detailsItem.network_detail"
                                :limit="10"
                                dataTestId="report-dialog__network-toggle"
                            >
                                <template #default="{ displayedItems }">
                                    <span v-for="(network, index) in displayedItems" :key="network.id" class="font-weight-semibold">
                                        {{ network.name }}<span v-if="index < displayedItems.length - 1">, </span>
                                    </span>
                                </template>
                            </ExpandableList>
                        </v-col>
                    </v-row>

                    <!-- Region (common) -->
                    <v-row no-gutters class="mb-2 align-center" v-if="detailsItem.region_detail" data-testid="report-dialog__region-row">
                        <v-col cols="12" sm="4" class="details-label" data-testid="report-dialog__region-label">{{  $nova.labelsName('reports_page','region_label','Region') }}</v-col>
                        <v-col cols="12" sm="8" class="details-value font-weight-semibold">
                            <ExpandableList
                                :items="detailsItem.region_detail"
                                :limit="10"
                                dataTestId="report-dialog__region-toggle"
                            >
                                <template #default="{ displayedItems }">
                                    <span v-for="(region, index) in displayedItems" :key="region.id" class="font-weight-semibold">
                                        {{ region.name }}<span v-if="index < displayedItems.length - 1">, </span>
                                    </span>
                                </template>
                            </ExpandableList>
                        </v-col>
                    </v-row>

                    <!-- Country (common) -->
                    <v-row no-gutters class="mb-2 align-center" v-if="detailsItem.country_detail" data-testid="report-dialog__country-row">
                        <v-col cols="12" sm="4" class="details-label" data-testid="report-dialog__country-label">{{  $nova.labelsName('reports_page','country_label','Country') }}</v-col>
                        <v-col cols="12" sm="8" class="details-value font-weight-semibold">
                            <ExpandableList
                                :items="detailsItem.country_detail"
                                :limit="10"
                                dataTestId="report-dialog__country-toggle"
                            >
                                <template #default="{ displayedItems }">
                                    <span v-for="(country, index) in displayedItems" :key="country.id" class="font-weight-semibold">
                                        {{ country.name }}<span v-if="index < displayedItems.length - 1">, </span>
                                    </span>
                                </template>
                            </ExpandableList>
                        </v-col>
                    </v-row>

                    <!-- Requested On (common) -->
                    <v-row no-gutters class="mb-2 align-center" data-testid="report-dialog__requested-on-row">
                        <v-col cols="12" sm="4" class="details-label" data-testid="report-dialog__requested-on-label">{{ $nova.labelsName('reports_page','commentary_requested_on_label','Requested On') }}</v-col>
                        <v-col cols="12" sm="8" class="details-value font-weight-semibold" data-testid="report-dialog__requested-on-value">
                            {{ formatDate(detailsItem.created_at) }}
                        </v-col>
                    </v-row>

                    <!-- Requested By (common) -->
                    <v-row no-gutters class="mb-2 align-center" v-if="detailsItem.user_detail" data-testid="report-dialog__requested-by-row">
                        <v-col cols="12" sm="4" class="details-label" data-testid="report-dialog__requested-by-label">{{ $nova.labelsName('reports_page','commentary_requested_by_label','Requested By') }}</v-col>
                        <v-col cols="12" sm="8" class="details-value font-weight-semibold">
                            <v-chip
                                color="''"
                                class="email-chip-bg"
                                outlined
                                text
                                :data-testid="`${testIdPrefix}__user-chip`"
                            >
                                <v-avatar left class="mr-2">
                                    <v-icon>mdi-account-outline</v-icon>
                                </v-avatar>
                                <div class="name-email">
                                    <template v-if="detailsItem.user_detail.name">
                                        <span data-testid="report-dialog__user-name">{{ detailsItem.user_detail.name }}</span> - 
                                    </template>
                                    <span class="textLowercase" data-testid="report-dialog__user-email">{{ detailsItem.user_detail.email }}</span>
                                </div>
                            </v-chip>
                        </v-col>
                    </v-row>
                </div>
            </div>
        </template>
    </lb-dialog>
</template>

<script>
import ExpandableList from './ExpandableList.vue';

export default {
    name: 'ReportDialog',

    components: {
        ExpandableList,
    },
    
    props: {
        /**
         * V-model binding for dialog visibility
         */
        value: {
            type: Boolean,
            required: true,
        },
        
        /**
         * Dialog title displayed in h5
         */
        title: {
            type: String,
            required: true,
        },
        
        /**
         * Details item object containing all report data
         */
        detailsItem: {
            type: Object,
            required: true,
        },
        
        /**
         * Test ID prefix for data-testid attributes
         */
        testIdPrefix: {
            type: String,
            default: 'report-dialog',
        },
        
        /**
         * Whether to show RU Type field
         */
        showRuType: {
            type: Boolean,
            default: true,
        },
    },
    
    methods: {
        /**
         * Formats ISO date string to "4th Dec - 2025, 1:36pm" format
         * @param {String} dateString - ISO date string
         * @returns {String} Formatted date string
         */
        formatDate(dateString) {
            if (!dateString) return '';
            
            const date = new Date(dateString);
            
            const day = date.getDate();
            const ordinal = (day) => {
                const s = ['th', 'st', 'nd', 'rd'];
                const v = day % 100;
                return day + (s[(v - 20) % 10] || s[v] || s[0]);
            };
            
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const month = months[date.getMonth()];
            
            const year = date.getFullYear();
            
            let hours = date.getHours();
            const minutes = date.getMinutes().toString().padStart(2, '0');
            const ampm = hours >= 12 ? 'pm' : 'am';
            hours = hours % 12 || 12;
            
            return `${ordinal(day)} ${month} - ${year}, ${hours}:${minutes}${ampm}`;
        },
    },
};
</script>

<style scoped>
.failed-reason {
    color: var(--status-red);
}
</style>
